import { useCallback, useEffect, useRef, useState } from 'react'
import { EdgeLabelRenderer, useReactFlow, useStore } from '@xyflow/react'
import {
  TaggedAnchor,
  Waypoint,
  OrthoSegment,
  nearestRawSegmentIndex,
  snapToGrid,
} from './waypointPath'
import { useWorkflowStore } from '@store/workflowStore'

interface WaypointAnchorsProps {
  edgeId: string
  sourceX: number
  sourceY: number
  targetX: number
  targetY: number
  waypoints: Waypoint[]
  anchors: TaggedAnchor[]
  segments: OrthoSegment[]
}

interface DragState {
  waypointIndex: number
  pointerId: number
  startX: number
  startY: number
  isPromotion: boolean
  hasMoved: boolean
  /** When dragging a segment, constrain movement to this axis only */
  constrainAxis?: 'x' | 'y'
  /** Fixed coordinate value on the constrained axis */
  constrainValue?: number
}

export function WaypointAnchors({
  edgeId,
  sourceX,
  sourceY,
  targetX,
  targetY,
  waypoints,
  anchors,
  segments,
}: WaypointAnchorsProps) {
  const zoom = useStore((s) => s.transform[2])
  const { screenToFlowPosition } = useReactFlow()
  const updateEdgeWaypoints = useWorkflowStore((s) => s.updateEdgeWaypoints)
  const pendingEdgeSplit = useWorkflowStore((s) => s.pendingEdgeSplit)
  const setPendingEdgeSplit = useWorkflowStore((s) => s.setPendingEdgeSplit)

  const dragStateRef = useRef<DragState | null>(null)
  // Mirror of the store's waypoints during drag so we avoid committing on
  // every pointer move. Committed on pointer up.
  const localWaypointsRef = useRef<Waypoint[] | null>(null)
  const [, forceRender] = useState(0)
  const bump = useCallback(() => forceRender((n) => n + 1), [])

  // Anchor sizing scales inversely with zoom so the handles stay visible at
  // any zoom level without becoming obnoxious when zoomed in.
  const anchorSize = Math.max(20, Math.round(24 / Math.max(zoom, 0.4)))

  // Segment overlay thickness in flow-space pixels (scales with zoom).
  // Wide hit area for easy grabbing on both desktop and mobile.
  const segmentThickness = Math.max(44, Math.round(48 / Math.max(zoom, 0.4)))

  // Recompute anchor positions from the local waypoints snapshot during drag
  // so the on-screen circles track the cursor without waiting for the store.
  const displayAnchors: TaggedAnchor[] = localWaypointsRef.current
    ? anchors.map((a) => {
        if (a.kind === 'waypoint' && typeof a.waypointIndex === 'number') {
          const live = localWaypointsRef.current![a.waypointIndex]
          if (live) return { ...a, x: live.x, y: live.y }
        }
        return a
      })
    : anchors

  const commit = useCallback(
    (next: Waypoint[]) => {
      updateEdgeWaypoints(edgeId, next, { pushUndo: true, markDirty: true })
    },
    [edgeId, updateEdgeWaypoints]
  )

  // --- Drag lifecycle (document-level listeners to survive DOM churn) ---

  const endDrag = useCallback(
    (commitFinal: boolean) => {
      const state = dragStateRef.current
      if (!state) return
      dragStateRef.current = null
      if (commitFinal && localWaypointsRef.current) {
        // If this was an elbow promotion but the user never dragged,
        // discard the waypoint — don't commit.
        if (!(state.isPromotion && !state.hasMoved)) {
          commit(localWaypointsRef.current)
        }
      }
      localWaypointsRef.current = null
      bump()
    },
    [commit, bump]
  )

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const state = dragStateRef.current
      if (!state || e.pointerId !== state.pointerId) return
      if (!localWaypointsRef.current) return

      // Require minimum drag distance before committing movement
      if (!state.hasMoved) {
        const dx = e.clientX - state.startX
        const dy = e.clientY - state.startY
        if (Math.hypot(dx, dy) < 5) return
        state.hasMoved = true
      }

      const flow = screenToFlowPosition({ x: e.clientX, y: e.clientY })
      const snapped = snapToGrid(flow.x, flow.y)
      // When dragging a segment, lock the constrained axis to keep the
      // segment moving strictly perpendicular to its direction.
      if (state.constrainAxis === 'x') snapped.x = state.constrainValue!
      else if (state.constrainAxis === 'y') snapped.y = state.constrainValue!
      const arr = localWaypointsRef.current.slice()
      arr[state.waypointIndex] = snapped
      localWaypointsRef.current = arr
      bump()
    }
    const onUp = (e: PointerEvent) => {
      const state = dragStateRef.current
      if (!state || e.pointerId !== state.pointerId) return
      endDrag(true)
    }
    const onCancel = (_e: PointerEvent) => endDrag(false)
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onCancel)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onCancel)
    }
  }, [bump, endDrag, screenToFlowPosition])

  // --- Anchor pointerdown handlers ---

  const handleWaypointDown = useCallback(
    (waypointIndex: number) => (e: React.PointerEvent<HTMLDivElement>) => {
      e.stopPropagation()
      e.preventDefault()
      dragStateRef.current = {
        waypointIndex,
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        isPromotion: false,
        hasMoved: false,
      }
      localWaypointsRef.current = waypoints.slice()
      setPendingEdgeSplit(null)
      bump()
    },
    [waypoints, setPendingEdgeSplit, bump]
  )

  // --- Segment drag handler ---
  // When the user grabs a segment overlay and drags, move the whole segment
  // by finding (or creating) the controlling waypoint and constraining the
  // drag to the perpendicular axis.

  const handleSegmentDown = useCallback(
    (seg: OrthoSegment) => (e: React.PointerEvent<HTMLDivElement>) => {
      e.stopPropagation()
      e.preventDefault()

      // Check if either endpoint of this segment matches an existing waypoint.
      // If so, drag that waypoint instead of inserting a new one — this avoids
      // creating two diverging control points that fold the path back on itself.
      const eps = 2
      let existingIdx = -1
      for (let w = 0; w < waypoints.length; w++) {
        const wp = waypoints[w]
        if (
          (Math.abs(wp.x - seg.x1) <= eps && Math.abs(wp.y - seg.y1) <= eps) ||
          (Math.abs(wp.x - seg.x2) <= eps && Math.abs(wp.y - seg.y2) <= eps)
        ) {
          existingIdx = w
          break
        }
      }

      let waypointIndex: number
      let newWaypoints: Waypoint[]

      if (existingIdx >= 0) {
        // Drag the existing waypoint that controls this segment
        waypointIndex = existingIdx
        newWaypoints = waypoints.slice()
      } else {
        // No existing waypoint on this segment — insert a new one at the midpoint
        const midX = (seg.x1 + seg.x2) / 2
        const midY = (seg.y1 + seg.y2) / 2
        const insertAt = seg.rawSegmentIndex
        newWaypoints = [
          ...waypoints.slice(0, insertAt),
          { x: midX, y: midY },
          ...waypoints.slice(insertAt),
        ]
        waypointIndex = insertAt
      }

      localWaypointsRef.current = newWaypoints
      // Constrain segment drag to the perpendicular axis:
      // dragging a horizontal segment → only Y changes (X locked)
      // dragging a vertical segment → only X changes (Y locked)
      const wp = newWaypoints[waypointIndex]
      dragStateRef.current = {
        waypointIndex,
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        isPromotion: existingIdx < 0,
        hasMoved: false,
        constrainAxis: seg.direction === 'H' ? 'x' : 'y',
        constrainValue: seg.direction === 'H' ? wp.x : wp.y,
      }
      setPendingEdgeSplit(null)
      bump()
    },
    [waypoints, setPendingEdgeSplit, bump]
  )

  const handleDoubleClick = useCallback(
    (waypointIndex: number) => (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation()
      e.preventDefault()
      const next = waypoints.filter((_, i) => i !== waypointIndex)
      commit(next)
    },
    [waypoints, commit]
  )

  // --- Click-to-split affordance ---

  const splitForThisEdge =
    pendingEdgeSplit && pendingEdgeSplit.edgeId === edgeId ? pendingEdgeSplit : null

  const handleSplitClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation()
      e.preventDefault()
      if (!splitForThisEdge) return
      const snapped = snapToGrid(splitForThisEdge.x, splitForThisEdge.y)
      const insertAt = nearestRawSegmentIndex(
        sourceX,
        sourceY,
        targetX,
        targetY,
        waypoints,
        snapped.x,
        snapped.y
      )
      const next = [
        ...waypoints.slice(0, insertAt),
        snapped,
        ...waypoints.slice(insertAt),
      ]
      commit(next)
      setPendingEdgeSplit(null)
    },
    [splitForThisEdge, sourceX, sourceY, targetX, targetY, waypoints, commit, setPendingEdgeSplit]
  )

  return (
    <EdgeLabelRenderer>
      {/* Draggable segment overlays — grab any line segment to bend it */}
      {segments.map((seg, i) => {
        const isH = seg.direction === 'H'
        const minX = Math.min(seg.x1, seg.x2)
        const minY = Math.min(seg.y1, seg.y2)
        const w = isH ? Math.abs(seg.x2 - seg.x1) : segmentThickness
        const h = isH ? segmentThickness : Math.abs(seg.y2 - seg.y1)
        const offsetX = isH ? minX : minX - segmentThickness / 2
        const offsetY = isH ? minY - segmentThickness / 2 : minY
        return (
          <div
            key={`seg-${i}`}
            className="nopan nodrag"
            onPointerDown={handleSegmentDown(seg)}
            title="Drag to bend"
            style={{
              position: 'absolute',
              transform: `translate(${offsetX}px, ${offsetY}px)`,
              width: w,
              height: h,
              cursor: isH ? 'ns-resize' : 'ew-resize',
              pointerEvents: 'all',
              touchAction: 'none',
              zIndex: 1099,
              // Invisible by default, subtle highlight on hover
              background: 'transparent',
              borderRadius: 4,
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => {
              ;(e.currentTarget as HTMLDivElement).style.background = 'rgba(25,118,210,0.15)'
            }}
            onMouseLeave={(e) => {
              ;(e.currentTarget as HTMLDivElement).style.background = 'transparent'
            }}
          />
        )
      })}

      {/* Waypoint dots — only show explicit waypoints, not elbows */}
      {displayAnchors
        .filter((a) => a.kind === 'waypoint')
        .map((a, i) => (
          <div
            key={`wp-${i}-${a.waypointIndex ?? 0}`}
            className="nopan nodrag"
            onPointerDown={handleWaypointDown(a.waypointIndex!)}
            onDoubleClick={handleDoubleClick(a.waypointIndex!)}
            title="Drag to move · double-click to remove"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${a.x}px, ${a.y}px)`,
              width: anchorSize,
              height: anchorSize,
              borderRadius: '50%',
              background: '#1976d2',
              border: '2px solid #fff',
              boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
              cursor: 'grab',
              pointerEvents: 'all',
              touchAction: 'none',
              zIndex: 1100,
            }}
          />
        ))}

      {splitForThisEdge && !dragStateRef.current && (
        <div
          className="nopan nodrag"
          onClick={handleSplitClick}
          onPointerDown={(e) => e.stopPropagation()}
          title="Add waypoint here"
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${splitForThisEdge.x}px, ${splitForThisEdge.y}px)`,
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: '#fff',
            border: '2px solid #1976d2',
            color: '#1976d2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
            fontWeight: 700,
            lineHeight: 1,
            cursor: 'pointer',
            boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
            pointerEvents: 'all',
            zIndex: 1101,
            userSelect: 'none',
          }}
        >
          +
        </div>
      )}
    </EdgeLabelRenderer>
  )
}
